import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

interface Option {
  value: string | number;
  label: string;
  sublabel?: string;
}

interface Props {
  options: Option[];
  value: string | number;
  onSelect: (value: string | number) => void;
  placeholder?: string;
  fontSize?: number;
}

const LocalSearchSelect: React.FC<Props> = ({ options, value, onSelect, placeholder = 'Search...', fontSize = 12 }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const selectedOption = options.find(o => String(o.value) === String(value));
  const selectedLabel = selectedOption ? selectedOption.label + (selectedOption.sublabel ? ' (' + selectedOption.sublabel + ')' : '') : '';

  const filtered = query.trim().length === 0
    ? options
    : options.filter(o =>
        o.label.toLowerCase().includes(query.toLowerCase()) ||
        (o.sublabel && o.sublabel.toLowerCase().includes(query.toLowerCase()))
      );

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        inputRef.current && !inputRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (highlight >= 0 && itemRefs.current[highlight]) {
      itemRefs.current[highlight]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlight]);

  const openDropdown = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    }
    setOpen(true);
    setQuery('');
    setHighlight(-1);
  };

  const handleSelect = (opt: Option) => {
    setQuery('');
    setOpen(false);
    setHighlight(-1);
    onSelect(opt.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') { openDropdown(); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight >= 0 && filtered[highlight]) { handleSelect(filtered[highlight]); }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
    }
  };

  return (
    <div style={{ position: 'relative', minWidth: 160 }}>
      <input
        ref={inputRef}
        className="form-control"
        style={{ fontSize }}
        placeholder={placeholder}
        value={open ? query : selectedLabel}
        onFocus={openDropdown}
        onChange={e => { setQuery(e.target.value); setHighlight(-1); }}
        onKeyDown={handleKeyDown}
      />
      {open && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999,
            background: '#ffffff', border: '1px solid var(--bdr)', borderRadius: 6,
            maxHeight: 260, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', marginTop: 2
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--tx3)' }}>No matches</div>
          ) : filtered.map((opt, idx) => (
            <div
              key={opt.value}
              ref={el => { itemRefs.current[idx] = el; }}
              onClick={() => handleSelect(opt)}
              onMouseEnter={() => setHighlight(idx)}
              style={{
                padding: '8px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid var(--bdr)',
                background: idx === highlight ? '#e8f0fe' : '#ffffff'
              }}
            >
              <div style={{ fontWeight: 600 }}>{opt.label}</div>
              {opt.sublabel && <div style={{ color: 'var(--tx3)', fontSize: 11 }}>{opt.sublabel}</div>}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default LocalSearchSelect;
